import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledTooltip } from '../../V2Common.styles';
import { ReactComponent as InfoCircleIcon } from '../../../../assets/icons/info_circle.svg';
import { ReactComponent as ResistanceIcon } from '../../../../assets/icons/resistance_icon.svg';
import { ReactComponent as SprayIcon } from '../../../../assets/icons/spray_icon.svg';
import { ReactComponent as HerbicideIcon } from '../../../../assets/icons/herbicideIcon.svg';
import { Divider } from 'syngenta-digital-cropwise-react-ui-kit';
import {
  Title,
  Container,
  SeedTechnologyContainer,
  SeedTechnologyTitle,
  TechnologyOptionsContainer,
  TechnologyOption,
  SectionHeader,
  SectionTitle,
  ProductsGrid,
  ProductCard,
  ProductInfo,
  ProductInfoLine,
  ProductLeftSection,
  ProductRightSection,
  WeedLink,
  IconWrapper,
  TechnologyFitContainer,
  TechnologyFitBar,
  TechnologyFitText,
  ResistanceContainer,
  ResistanceTitle,
  ResistanceText,
  ResistanceList,
  ResistanceItem,
  ApplicationContainer,
  ApplicationHeader,
  ApplicationTitle,
  ApplicationType,
  ApplicationProductsGrid,
  WeedControlTitle,
  WeedControlGrid,
  WeedCard,
  WeedName,
  ControlInfo,
  ControlPercentage,
  ApplicationFitBar,
  ProductCardTotal,
  SprayIconWrapper,
  ProductName,
  ProductRate,
  ProductUnit,
  Text,
} from './HerbicideTechnology.styles';

interface Product {
  name: string;
  rate: string | number;
  unit: string;
  totalQuantity?: number;
  finalUnit?: string; // The calculated unit for display with total quantity
}

interface HerbicideStrategy {
  timing: string;
  products: Product[];
}

interface SingleWeedEfficacyEntry {
  efficacy?: number;
  eppo_code?: string;
}

interface StrategyOption {
  type?: string;
  attribute?: {
    efficacy?: number;
    herbicide_strategies?: HerbicideStrategy[];
    single_weed_efficacy?: SingleWeedEfficacyEntry[];
    dicot_resistance_management?: number;
    grass_resistance_management?: number;
  };
  isChecked?: boolean;
}

interface WeedEfficacyInfo {
  name: string;
  density: number;
}

interface WeedInfo {
  isChecked: boolean;
  density: number;
}

interface HerbicideTechnologyProps {
  sectionRef?: (node?: Element | null) => void;
  recommendationCropwise?: any;
  isDDG?: boolean;
}

const HerbicideTechnology: React.FC<HerbicideTechnologyProps> = ({
  sectionRef,
  recommendationCropwise,
  isDDG,
}) => {
  const [t] = useTranslation();
  const [productsExpanded, setProductsExpanded] = useState(true);

  // Helper function to calculate the final unit based on the original unit
  const calculateFinalUnit = (originalUnit: string): string => {
    // Check if the unit has a per hectare format (e.g., 'g/ha', 'l/ha')
    const perHectareMatch = originalUnit.match(/^([a-zA-Z]+)\/ha$/);
    if (perHectareMatch) {
      // Extract the base unit (e.g., 'g', 'l')
      const baseUnit = perHectareMatch[1];
      switch (baseUnit.toLowerCase()) {
        case 'g':
          return 'g';
        case 'kg':
          return 'kg';
        case 'l':
          return 'l';
        case 'ml':
          return 'mL';
        default:
          return baseUnit;
      }
    }

    // For units that are not per hectare, return as is
    return originalUnit || 'L'; // Default to 'L' if no unit is provided
  };

  // Trait mapping from API values to display names
  const traitMapping: { [key: string]: string } = {
    IMI: 'Clearfield®',
    'IMI+': 'Clearfield® Plus',
    Sulfo: 'Sulfonylurea-tolerant',
    Conventional: 'Conventional',
    AIR: 'A.I.R.™',
    Express: 'Express®',
  };

  // Extract traits data from recommendationCropwise
  const traits =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs?.traits ||
    [];

  // Map API trait values to display names
  const mappedTraits = traits.map((trait: string) => traitMapping[trait] || trait);

  // Extract strategy data from recommendationCropwise
  const strategyList =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs
      ?.strategyList;
  const strategyOptions = strategyList?.default as Record<string, StrategyOption> | undefined;
  const fallbackSelectedStrategy = strategyOptions
    ? Object.keys(strategyOptions).find((strategyKey) => strategyOptions[strategyKey]?.isChecked)
    : undefined;
  const selectedStrategy = strategyList?.selected || fallbackSelectedStrategy;
  const selectedStrategyData = selectedStrategy ? strategyOptions?.[selectedStrategy] : undefined;
  const efficacy = selectedStrategyData?.attribute?.efficacy || 0;
  const herbicideStrategies = selectedStrategyData?.attribute?.herbicide_strategies || [];

  // Extract weed efficacy data from strategy data
  const singleWeedEfficacy = selectedStrategyData?.attribute?.single_weed_efficacy || [];

  // Get inputWeedBasket from agronomicInputs
  const inputWeedBasket =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs
      ?.inputWeedBasket?.selected || {};

  // Convert single weed efficacy data to array for rendering, using english_name if available
  const weedData: WeedEfficacyInfo[] = singleWeedEfficacy.map((weedInfo: any) => {
    const eppo = weedInfo.eppo_code;
    const basket = inputWeedBasket[eppo];
    return {
      name: basket?.local_name || eppo,
      density: Math.round((weedInfo.efficacy || 0) * 10),
    };
  });

  // Calculate total area from config.recommendation
  const configRecommendation =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.recommendation || [];
  const totalArea = configRecommendation.reduce((sum: number, field: any) => {
    // Use declared_area from the field properties if available, otherwise try estimatedSize or calculated_area
    const fieldArea = field?.declared_area || field?.estimatedSize || field?.calculated_area || 0;
    return sum + fieldArea;
  }, 0);

  // Get field areas from geometry properties as fallback
  const geometryProperties =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.geometry?.properties || [];
  const fallbackTotalArea =
    totalArea > 0
      ? totalArea
      : geometryProperties.reduce((sum: number, property: any) => {
        const propertyArea =
            property?.fields?.reduce((propSum: number, field: any) => {
              return propSum + (field?.declared_area || field?.calculated_area || 0);
            }, 0) || 0;
        return sum + propertyArea;
      }, 0);

  const finalTotalArea = fallbackTotalArea > 0 ? fallbackTotalArea : 1; // Fallback to 1 to avoid division by zero

  // Extract unique products from all herbicide strategies
  const allProducts = herbicideStrategies.flatMap(
    (strategy: HerbicideStrategy) => strategy.products
  );
  const uniqueProducts = allProducts.reduce((unique: Product[], product: Product) => {
    const exists = unique.find((p) => p.name === product.name);
    // Calculate the final unit for total quantity
    const finalUnit = calculateFinalUnit(product.unit);

    if (!exists) {
      // Calculate total quantity by multiplying rate by total area
      const totalQuantity = (Number(product.rate) || 0) * finalTotalArea;
      unique.push({
        ...product,
        totalQuantity: totalQuantity,
        rate: totalQuantity, // Store total quantity in rate field for display
        finalUnit: finalUnit, // Store the calculated final unit
      });
    } else {
      // If product exists, sum up the total quantities
      const additionalQuantity = (Number(product.rate) || 0) * finalTotalArea;
      exists.totalQuantity = (exists.totalQuantity || 0) + additionalQuantity;
      exists.rate = exists.totalQuantity; // Update rate to show total quantity
      exists.finalUnit = finalUnit; // Ensure the unit is set (in case it wasn't before)
    }
    return unique;
  }, []);

  // Check if we should render the section at all
  // Hide the section if:
  // 1. It's a DDG solution
  // 2. There are no mapped traits (no seed technologies)
  // 3. There are mapped traits but no herbicide products to display
  if (isDDG || mappedTraits.length === 0 || uniqueProducts.length === 0) {
    // Return an empty div with the ref to maintain the DOM structure
    return <div ref={sectionRef} />;
  }

  return (
    <div ref={sectionRef}>
      <Title>{t('Herbicide Technology & Weed Control')}</Title>
      <Text>
        {t(
          'Here you can find the recommended herbicide strategy to use on your fields based on the weeds added to the weed basket.'
        )}
      </Text>

      <Container>
        <SeedTechnologyContainer>
          <SeedTechnologyTitle>{t('Applicable Seeds Technology')}</SeedTechnologyTitle>
          <TechnologyOptionsContainer>
            {mappedTraits.map((trait: string, index: number) => (
              <TechnologyOption key={index}>{trait}</TechnologyOption>
            ))}
          </TechnologyOptionsContainer>
        </SeedTechnologyContainer>

        <SectionHeader>
          <SectionTitle>{t('All Herbicides and Total Quantity Recommended')}</SectionTitle>
        </SectionHeader>

        <ProductsGrid>
          {uniqueProducts.length > 0 ? (
            uniqueProducts.map((product: Product, index: number) => (
              <ProductCardTotal key={index}>
                <ProductInfo>{product.name}</ProductInfo>
                <ProductInfo>
                  <SprayIconWrapper>
                    <HerbicideIcon />
                  </SprayIconWrapper>
                  <ProductRate>{Number(product.rate).toFixed(1)}</ProductRate>
                  <span style={{ margin: '0 4px' }} />
                  <ProductUnit style={{ color: 'black', paddingTop: '1px' }}>
                    {t(product.finalUnit || 'Ltr')}
                  </ProductUnit>
                </ProductInfo>
              </ProductCardTotal>
            ))
          ) : (
            <ProductCardTotal>
              <ProductInfo>{t('No products available')}</ProductInfo>
              <ProductInfo>-</ProductInfo>
            </ProductCardTotal>
          )}
        </ProductsGrid>

        <div
          style={{ marginBottom: '10px', fontSize: '14px', fontStyle: 'italic', color: '#696f88' }}
        >
          {t(
            'Please use this as a reference for the quantity of product(s) needed based on the area from the field(s) included in this recommendation'
          )}
        </div>

        <SectionTitle>{t('Application Strategy')}</SectionTitle>
        <TechnologyFitContainer>
          <TechnologyFitBar />
          <TechnologyFitText>
            {t('Technology fit:')} {Math.round(efficacy)}%
          </TechnologyFitText>
          <StyledTooltip
            title={t(
              'pop-up text in gray border when you have the cursor on Strategy fit_description'
            )}
            placement="top"
          >
            <InfoCircleIcon style={{ marginLeft: '4px', cursor: 'pointer' }} />
          </StyledTooltip>
        </TechnologyFitContainer>

        <ResistanceContainer>
          <ResistanceTitle>
            <ResistanceIcon />
            <ResistanceText>{t('Resistance')}</ResistanceText>
          </ResistanceTitle>
          <ResistanceList>
            {/* Dynamically render resistance management effectiveness for DICOTS and GRASSES */}
            {(() => {
              const dicotRes = selectedStrategyData?.attribute?.dicot_resistance_management;
              const grassRes = selectedStrategyData?.attribute?.grass_resistance_management;
              const resistanceTextMap: Record<number, string> = {
                1: t('highly effective resistance management solution against DICOT'),
                2: t('moderately effective resistance management solution against DICOT'),
                3: t('less effective resistance management solution against DICOT'),
              };
              const resistanceTextMapGrass: Record<number, string> = {
                1: t('highly effective resistance management solution against GRASSES'),
                2: t('moderately effective resistance management solution against GRASSES'),
                3: t('less effective resistance management solution against GRASSES'),
              };
              return (
                <>
                  {dicotRes && resistanceTextMap[dicotRes] && (
                    <ResistanceItem>{resistanceTextMap[dicotRes]}</ResistanceItem>
                  )}
                  {grassRes && resistanceTextMapGrass[grassRes] && (
                    <ResistanceItem>{resistanceTextMapGrass[grassRes]}</ResistanceItem>
                  )}
                </>
              );
            })()}
          </ResistanceList>
        </ResistanceContainer>

        {herbicideStrategies.map((strategy: HerbicideStrategy, index: number) => (
          <ApplicationContainer key={index}>
            <ApplicationHeader>
              <ApplicationTitle>
                {t('Application')} {index + 1}
              </ApplicationTitle>
              <ApplicationType>{t(strategy.timing)}</ApplicationType>
            </ApplicationHeader>
            <ApplicationProductsGrid>
              {strategy.products.map((product: Product, productIndex: number) => (
                <ProductCard key={productIndex}>
                  <ProductInfoLine>
                    <ProductLeftSection>
                      <ApplicationFitBar />
                      <ProductName>{product.name}</ProductName>
                    </ProductLeftSection>
                    <ProductRightSection>
                      <SprayIconWrapper>
                        <SprayIcon />
                      </SprayIconWrapper>
                      <ProductRate>{product.rate}</ProductRate>{' '}
                      <ProductUnit style={{ paddingTop: '2px' }}>{product.unit}</ProductUnit>
                    </ProductRightSection>
                  </ProductInfoLine>
                </ProductCard>
              ))}
            </ApplicationProductsGrid>
          </ApplicationContainer>
        ))}
      </Container>

      {weedData.length > 0 && (
        <Container>
          <WeedControlTitle>{t('Level of control for the selected weed basket')}</WeedControlTitle>
          <WeedControlGrid>
            {weedData.map((weed: WeedEfficacyInfo, index: number) => {
              return (
                <WeedCard key={index}>
                  <WeedName>{weed.name}</WeedName>
                  <ControlInfo>
                    <ApplicationFitBar />
                    <span
                      style={{
                        marginLeft: '4px',
                        fontSize: '12px',
                        color: '#696f88',
                      }}
                    >
                      {t('Expected Control:')}
                    </span>
                    <ControlPercentage
                      color={
                        weed.density < 50
                          ? '#9C2628' // red
                          : weed.density < 70
                            ? '#744A0B' // amber
                            : '#0C612C' // green
                      }
                      style={{
                        background:
                          weed.density < 50
                            ? 'var(--Red-red-20, #FFD9D9)'
                            : weed.density < 70
                              ? 'var(--Yellow-yellow-20, #FFE4AE)'
                              : 'var(--Green-green-20, #C3EAD1)',
                        borderRadius: '4px',
                        border:
                          weed.density < 50
                            ? '1px solid var(--Red-red-20, #FFD9D9)'
                            : weed.density < 70
                              ? '1px solid var(--Yellow-yellow-20, #FFE4AE)'
                              : '1px solid var(--Green-green-20, #C3EAD1)',
                        padding: '2px 8px',
                        minWidth: '40px',
                        display: 'inline-block',
                        textAlign: 'center',
                      }}
                    >
                      {weed.density}%
                    </ControlPercentage>
                  </ControlInfo>
                </WeedCard>
              );
            })}
          </WeedControlGrid>
        </Container>
      )}
      <Divider />
    </div>
  );
};

export default HerbicideTechnology;
